import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import LangDropdown from './LangDropdown';
import RegisterForm from './Register';
import AnimatedCounter from './AnimatedCounter';
import { API_URL } from '../config';
import { t } from '../i18n';
import './UserLogin.css';

// ── Хук: загальна кількість записів у фонді ──────────────
function useTotalCount() {
  const LSKEY = 'irbis_total_count';
  const [total, setTotal] = useState(() => {
    try { const v = Number(localStorage.getItem(LSKEY)); return v > 0 ? v : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/databases.php?action=counts`)
      .then(r => r.json())
      .then(d => {
        if (typeof d.total === 'number') {
          setTotal(d.total);
          try { localStorage.setItem(LSKEY, String(d.total)); } catch {}
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return { total, loading };
}

// ── Запит входу — спільний для сторінки входу і вікна входу (LoginModal.js) ──
export async function requestLogin(body) {
  const res = await fetch(`${API_URL}/login.php`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  return res.json();
}

// ── Попередження про застарілість контенту фонду — однаково рендериться
// і в лівій панелі (десктоп), і в мобільній нижній інфо-панелі ──
function LpNotice({ lang }) {
  return (
    <div className="lp-notice">
      <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
      <span>
        {t(lang, 'lp_notice_p1')}
      </span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// LOGIN PAGE — єдиний export цього файлу. Рендериться в корені App.js,
// коли користувач ще не авторизований. Містить одразу і картку входу
// (розмітка нижче, як і раніше), і картку реєстрації (окремий компонент
// RegisterForm з Register.js) — вони перемикаються без переходу на інший
// "маршрут"/сторінку, просто підміняється вміст всередині lp-card-slot
// (детальніше — коментарі біля authView/switching нижче).
// ═══════════════════════════════════════════════════════════
// onCancelRegister — є лише коли реєстрацію відкрив гість з каталогу (App.js):
// кругла «назад» форми реєстрації повертає його в каталог, а не на вхід
function LoginPage({ onLogin, darkMode, onToggleDark, lang, onLangChange, initialView, onCancelRegister }) {
  const [loginVal,      setLoginVal]      = useState('');
  const [password,      setPassword]      = useState('');
  const [showPwd,       setShowPwd]       = useState(false);
  const [loading,       setLoading]       = useState(false);
  // Якщо сюди привів примусовий розлогін через протухлий reader_token
  // (readerFetch у apiAuth.js, TTL 8 год) — одразу показуємо зрозуміле
  // повідомлення замість мовчазної форми входу без пояснення, чому читача
  // раптом викинуло.
  const [error,         setError]         = useState(() => {
    try {
      if (sessionStorage.getItem('irbis_session_expired')) {
        sessionStorage.removeItem('irbis_session_expired');
        return t(lang, 'session_expired_msg');
      }
    } catch {}
    return '';
  });
  const [logoErr,       setLogoErr]       = useState(false);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  // ── Перемикання карток "вхід" ⇄ "реєстрація" ─────────────────────────────
  // authView   — яка картка зараз ЗАКРІПЛЕНА: 'login' або 'register'. За цим
  //              значенням тернарник у JSX нижче (біля коментаря "КАРТКА")
  //              вирішує, що саме рендерити — <div className="lp-card"> (вхід)
  //              чи <RegisterForm> (реєстрація).
  // switching  — чи триває зараз плавний перехід (true — рівно на час анімації,
  //              потім автоматично повертається у false). Додає обгортці
  //              lp-card-slot клас lp-card-slot--switching (стилі й keyframes —
  //              у UserLogin.css) — саме він ховає картку перед підміною вмісту
  //              і повертає видимість (із пружинним "приземленням") після.
  const [authView,  setAuthView]  = useState(initialView === 'register' ? 'register' : 'login'); // 'login' | 'register'
  const [switching, setSwitching] = useState(false);

  // Открываем панель один раз за сессию, с задержкой как у cookie-баннера
  useEffect(() => {
    let alreadyShown = true; // якщо sessionStorage недоступний (приватний режим,
    try { alreadyShown = !!sessionStorage.getItem('lp_info_panel_shown'); } catch {} // заблоковані дані) — просто не відкриваємо панель авто
    if (!alreadyShown && window.innerWidth < 992) {
      const t = setTimeout(() => setInfoPanelOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  const [panelClosing, setPanelClosing] = useState(false);

const openInfoPanel  = () => { setPanelClosing(false); setInfoPanelOpen(true); };
  const closeInfoPanel = () => {
    try { sessionStorage.setItem('lp_info_panel_shown', '1'); } catch {}
    setPanelClosing(true);
    setTimeout(() => { setInfoPanelOpen(false); setPanelClosing(false); }, 260);
  };
  // switchView(view) — ЄДИНА точка входу для переходу login ⇄ register.
  // Використовується у двох місцях нижче: goToRegister (кнопка "Зареєструватися"
  // на картці входу) і goToLogin (кнопка "назад" на картці реєстрації, а також
  // кнопка на екрані "Заявку прийнято" в Register.js — прокидається туди пропсом onBack).
  //
  // Хореографія (щоб не було миттєвого "рвучого" стрибка контенту):
  //   1) setSwitching(true) → CSS одразу починає гасити .lp-card-slot
  //      (transition ~50ms, див. .lp-card-slot--switching у UserLogin.css);
  //   2) через 50ms (setTimeout) — рівно стільки, скільки триває CSS-згасання —
  //      підміняємо authView. Саме в ЦЕЙ момент React реально розмонтовує стару
  //      картку і монтує нову, але картка досі невидима (opacity: 0), тому
  //      підміна вмісту глядачу не видна;
  //   3) requestAnimationFrame ×2 — чекаємо, поки браузер справді відмалює кадр
  //      із НОВИМ вмістом у невидимому стані (перший rAF), і лише в НАСТУПНОМУ
  //      кадрі (другий rAF) знімаємо switching. Без цього подвійного чекання
  //      браузер міг би "не помітити" проміжний невидимий кадр і показати
  //      кінцевий результат одразу, без анімації;
  //   4) switching=false → клас lp-card-slot--switching зникає → вмикається
  //      пружинна анімація lpCardSpring (CSS-keyframes) — картка "приземляється".
  // Захист від спаму: якщо перехід уже триває (switching) або просять перейти
  // на вже активний вигляд — нічого не робимо.
  const switchView = (view) => {
    if (switching || view === authView) return;
    setSwitching(true);
    setTimeout(() => {
      setAuthView(view);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setSwitching(false));
      });
    }, 50);
  };
  // Прості обгортки для читабельності в JSX (onClick={goToRegister} замість
  // onClick={() => switchView('register')})
  const goToRegister = () => switchView('register');
  const goToLogin    = () => switchView('login');

  // Блокуємо скролл body ТІЛЬКИ на десктопі (split-screen лейаут) —
  // на мобілці сторінка одноколонкова і природно вища за viewport,
  // їй потрібен звичайний скрол сторінки (поріг синхронізований з CSS: 967.98px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 968px)');
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
      document.documentElement.style.overflow = mq.matches ? 'hidden' : '';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  const { total: totalCount, loading: countLoading } = useTotalCount();

  const doLogin = async (isGuest = false) => {
    setLoading(true); setError('');
    try {
      const data = await requestLogin(isGuest ? { guest: true } : { login: loginVal, password });
      if (data.success) onLogin(data);
      else setError(data.message || t(lang, 'login_err_generic'));
    } catch {
      setError(t(lang, 'login_err_conn'));
    } finally { setLoading(false); }
  };

  const today = new Date().toLocaleDateString('uk-UA', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="lp">
      {/* Фон */}
      <div
        className="lp-bg"
        style={{ backgroundImage: `url("${process.env.PUBLIC_URL}/images/libbackground.jpg")` }}
      />
      <div className="lp-bg-tint" />

      {/* ═══ ЛІВА ПАНЕЛЬ ═══ */}
      <aside className="lp-left">

        <div className="lp-brand-top">
          <div className="lp-logo-row">
            <div className="lp-logo-icon">
              <img
                src={`${process.env.PUBLIC_URL}/images/liblogo.png`}
                alt={t(lang, 'lib_logo_alt')}
                className="lp-logo-img"
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <div className="lp-brand-name">{t(lang, 'lp_university_name')}</div>
              <div className="lp-brand-uni">{t(lang, 'home_res_library')}</div>
            </div>
          </div>

          <div className="lp-info">

            <div className="lp-services-block">
              <div className="lp-services-label">{t(lang, 'lp_services_label')}</div>
              <ul className="lp-services-list">
                <li><i className="fa-solid fa-book-open" aria-hidden="true" />{t(lang, 'lp_service_1')}</li>
                <li><i className="fa-solid fa-basket-shopping" aria-hidden="true" />{t(lang, 'lp_service_2')}</li>
                <li><i className="fa-solid fa-address-card" aria-hidden="true" />{t(lang, 'lp_service_3')}</li>
                <li><i className="fa-brands fa-internet-explorer" aria-hidden="true" />{t(lang, 'lp_service_4')}</li>
              </ul>
            </div>

            <LpNotice lang={lang} />

          </div>{/* /lp-info */}

<nav className="lp-links">
  <a href="https://lib.udu.edu.ua" target="_blank" rel="noreferrer" className="lp-link" title={t(lang, 'home_res_library')}>
    <i className="fa-solid fa-building-columns fa-fw" />
    <span className="lp-link-label">{t(lang, 'home_res_library')}</span>
    <i className="fa-solid fa-arrow-up-right-from-square lp-link-arrow" />
  </a>
  <a href="https://enpuir.udu.edu.ua/home" target="_blank" rel="noreferrer" className="lp-link" title={t(lang, 'home_res_repo')}>
    <i className="fa-solid fa-box-archive fa-fw" />
    <span className="lp-link-label">{t(lang, 'home_res_repo')}</span>
    <i className="fa-solid fa-arrow-up-right-from-square lp-link-arrow" />
  </a>
  <a href="https://libdc.udu.edu.ua/" target="_blank" rel="noreferrer" className="lp-link" title={t(lang, 'home_res_digital')}>
    <i className="fa-solid fa-photo-film fa-fw" />
    <span className="lp-link-label">{t(lang, 'home_res_digital')}</span>
    <i className="fa-solid fa-arrow-up-right-from-square lp-link-arrow" />
  </a>
</nav>
        </div>

        <div className="lp-brand-bottom">
          <div className="lp-chips">
            <div className="lp-chip lp-chip--counter">
              <i className="fa-solid fa-layer-group" />
              {countLoading && !totalCount
                ? <span className="lp-chip-loading">…</span>
                : <>
                    <AnimatedCounter target={totalCount || 0} duration={1800} />
                    &nbsp;<span className="lp-chip-counter-label">{t(lang, 'records_fund')}</span>
                  </>
              }
            </div>
            <div className="lp-chip">
              <i className="fa-solid fa-circle-exclamation"></i>{t(lang, 'lp_errors_label')}&nbsp;
              <a href="mailto:libsupport@npu.edu.ua" className="lp-notice-link">libsupport@npu.edu.ua</a>
            </div>
          </div>
          <div className="lp-powered">
            <i className="fa-regular fa-calendar" />
            <span>{today}</span>
            <span><strong>{t(lang, 'lp_copyright')}</strong></span>
          </div>
        </div>
      </aside>

      {/* ═══ ПРАВА ПАНЕЛЬ — форма ═══ */}
      <main className="lp-right">

        {/* Мобільний заголовок + перемикач теми — на екрані реєстрації йдуть в один
            рядок (lp-mobile-top-row--compact), щоб форма піднялась вище; на вході
            lp-mobile-top-row нічого не міняє (display:contents), поведінка та сама,
            що й раніше (шапка колонкою, перемикач нижче праворуч) */}
        <div className={`lp-mobile-top-row${authView === 'register' ? ' lp-mobile-top-row--compact' : ''}`}>
          <div className={`lp-mobile-header${authView === 'register' ? ' lp-mobile-header--compact' : ''}`}>
            <div className="lp-logo-icon lp-logo-icon--sm">
              {!logoErr ? (
                <img
                  src={`${process.env.PUBLIC_URL}/images/liblogo.png`}
                  alt={t(lang, 'lib_logo_alt')}
                  className="lp-logo-img lp-logo-img--sm"
                  onError={() => setLogoErr(true)}
                />
              ) : (
                <i className="fa-solid fa-book-open" style={{ fontSize: '20px', color: '#fff' }} />
              )}
            </div>
            <div>
              <div className="lp-brand-name lp-brand-name--mobile">{t(lang, 'lp_university_name')}</div>
              <div className="lp-brand-uni lp-brand-uni--mobile">{t(lang, 'home_res_library')}</div>
            </div>
          </div>

          {/* Перемикач теми — на мобілі йде одразу під шапкою, в потоці (не fixed);
              на десктопі лишається fixed у верхньому правому куті (CSS-медіазапит) */}
          <div className="lp-theme-switcher">
            <LangDropdown lang={lang} onChange={onLangChange} />
            <button
              className="app-navbar-icon-btn app-navbar-theme-toggle"
              onClick={onToggleDark}
              aria-label={darkMode ? t(lang, 'theme_light') : t(lang, 'theme_dark')}
              title={darkMode ? t(lang, 'theme_to_light') : t(lang, 'theme_to_dark')}
            >
              <i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />
            </button>
          </div>
        </div>

        {/* КАРТКА — lp-card-slot НІКОЛИ не розмонтовується (на відміну від вмісту
            всередині) — саме на НІЙ висить CSS-перехід opacity/scale (crossfade),
            керований станом switching вище. Клас --switching додається на короткий
            час під час переходу (див. switchView). Тернарник нижче вирішує, що
            саме зараз змонтовано: картку входу (лишається в цьому ж файлі, як і
            раніше) чи форму реєстрації (окремий компонент з Register.js). */}
        <div className={`lp-card-slot${switching ? ' lp-card-slot--switching' : ''}`}>
        {authView === 'login' ? (
        <div className="lp-card">

          <div className="lp-card-head">
            <div className="lp-card-icon">
              <i className="fa-solid fa-right-to-bracket" />
            </div>
            <div>
              <div className="lp-card-title">{t(lang, 'login_title')}</div>
              <div className="lp-card-sub">{t(lang, 'login_sub')}</div>
            </div>
          </div>

          {error && (
            <div className="lp-alert">
              <i className="fa-solid fa-circle-exclamation" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          )}

          <div className="lp-field">
            <label className="lp-label" htmlFor="lp-login">{t(lang, 'login_login')}</label>
            <div className="lp-igroup">
              <span className="lp-ipfx"><i className="fa-solid fa-user" /></span>
              <input
                id="lp-login"
                className="lp-input"
                type="text"
                name="username"
                placeholder={t(lang, 'login_placeholder')}
                value={loginVal}
                onChange={e => setLoginVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doLogin(false)}
                autoComplete="username"
              />
            </div>
          </div>

          <div className="lp-field lp-field--pwd">
            <label className="lp-label" htmlFor="lp-pwd">{t(lang, 'login_pwd')}</label>
            <div className="lp-igroup">
              <span className="lp-ipfx"><i className="fa-solid fa-lock" /></span>
              <input
                id="lp-pwd"
                className="lp-input"
                type={showPwd ? 'text' : 'password'}
                name="password"
                placeholder={t(lang, 'pwd_placeholder')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doLogin(false)}
                autoComplete="current-password"
              />
              <button className="lp-eye" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
          </div>

          <button
            className="lp-btn lp-btn-main"
            onClick={() => doLogin(false)}
            disabled={loading}
          >
            {loading
              ? <><Spinner size="sm" animation="border" className="me-2" />{t(lang, 'login_entering')}</>
              : <><i className="fa-solid fa-right-to-bracket" /> {t(lang, 'login_btn')}</>
            }
          </button>
          <div className="lp-or">
            <a className="help" href="https://lib.udu.edu.ua/posluhy/dopomoha-onlain" target="_blank" rel="nofollow noopener noreferrer">
              <span>{t(lang, 'login_forgot')}</span>
            </a>
          </div>
          <button className="lp-btn lp-btn-guest" onClick={() => doLogin(true)} disabled={loading}>
            <i className="fa-solid fa-user-slash" />
            {t(lang, 'login_guest')}
          </button>

          <div className="lp-or"><span>{t(lang, 'login_or')}</span></div>

          {/* Клас lp-btn спільний з кнопками "Увійти"/"гість" вище (lp-btn-main/lp-btn-guest) —
              базові розміри/шрифт з одного місця, тут лише інше поєднання модифікаторів */}
          <button className="lp-btn lp-btn-reg" onClick={goToRegister}>
            <i className="fa-solid fa-user-plus" />
            {t(lang, 'login_register')}
          </button>

        </div>
        ) : (
          /* onBack={goToLogin} — той самий switchView, той самий плавний перехід,
             просто у зворотному напрямку (register → login) */
          <RegisterForm lang={lang} onBack={goToLogin} onCancel={onCancelRegister} onLogin={onLogin} />
        )}
        </div>
        {/* /lp-card-slot */}

        {/* Кнопка відкриття інфо-панелі (тільки мобіл) */}
        <button className="lp-info-trigger d-flex d-lg-none" onClick={openInfoPanel} type="button">
          <i className="fa-solid fa-circle-info" aria-hidden="true" />
          {t(lang, 'lp_info_title')}
        </button>

        {/* Нижня інфо-панель (slide-up, cookie-style) */}
        {infoPanelOpen && createPortal(
          <>
            <div className="lp-info-panel-overlay" onClick={closeInfoPanel} />
            <div className={`lp-info-panel${panelClosing ? ' lp-info-panel--closing' : ''}`} role="dialog" aria-modal="true" aria-label={t(lang, 'lp_info_title')}>
              <div className="lp-info-panel-handle" />
              <div className="lp-info-panel-head">
                <span className="lp-info-panel-title">
                  <i className="fa-solid fa-circle-info me-2" aria-hidden="true" />
                  {t(lang, 'lp_info_title')}
                </span>
                <button className="lp-info-panel-close" onClick={closeInfoPanel} aria-label={t(lang, 'sidebar_close')}>
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="lp-info-panel-body">
                <LpNotice lang={lang} />
              </div>
            </div>
          </>,
          document.body
        )}

      </main>
    </div>
  );
}

export default LoginPage;
